/**
 * HEAREN V1 - UNIFIED PLAYER COMPONENT
 * Single source of truth for all player contexts (Radio, Soundscape, Playlist)
 * 
 * LOCKED FOR V1 - DO NOT MODIFY WITHOUT EXPLICIT APPROVAL
 * 
 * Structure canonique:
 * 1. Header: Back button, Icon + context name, Favorite button
 * 2. Central visual zone: Wave animation
 * 3. Sound title: display_name (never filename)
 * 4. Transport controls: ⏪ ⏯️ ⏩, Timeline, elapsed/remaining
 * 5. Profil audio entry: Single clickable line "Profil audio · écoute personnalisée (acouphènes)" -> opens Spectrum modal
 *    (Toggles Acouphènes/Profil audio are ONLY in the modal, not in the player)
 * 6. Footer controls: Volume, Stop
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Star, 
  Play, 
  Pause, 
  SkipForward, 
  Square,
  Volume2,
  VolumeX,
  Lock,
  Settings,
  MoreVertical,
} from 'lucide-react';
import './unifiedPlayer.css';
import { formatDb, formatFrequency, getTinnitusBadgeInfo } from '@/utils/tinnitusDisplay';

// Wave Animation Component
const WaveAnimation = ({ isPlaying, variant = 'default' }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const phaseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      if (isPlaying) {
        phaseRef.current += 0.02;
      }

      // Wave parameters based on variant
      const waveConfig = {
        radio: { amplitude: 30, frequency: 0.015, layers: 3, color: 'rgba(139, 92, 246, 0.6)' },
        soundscape: { amplitude: 25, frequency: 0.012, layers: 4, color: 'rgba(34, 197, 94, 0.5)' },
        playlist: { amplitude: 20, frequency: 0.018, layers: 3, color: 'rgba(59, 130, 246, 0.5)' },
        default: { amplitude: 25, frequency: 0.015, layers: 3, color: 'rgba(139, 92, 246, 0.5)' }
      };

      const config = waveConfig[variant] || waveConfig.default;

      // Draw multiple wave layers
      for (let layer = 0; layer < config.layers; layer++) {
        const layerPhase = phaseRef.current + (layer * Math.PI / config.layers);
        const layerAmplitude = config.amplitude * (1 - layer * 0.2);
        const opacity = 0.3 + (layer * 0.15);

        ctx.beginPath();
        ctx.moveTo(0, height / 2);

        for (let x = 0; x <= width; x++) {
          const y = height / 2 + 
            Math.sin(x * config.frequency + layerPhase) * layerAmplitude * (isPlaying ? 1 : 0.3) +
            Math.sin(x * config.frequency * 2 + layerPhase * 1.5) * (layerAmplitude * 0.3);
          ctx.lineTo(x, y);
        }

        ctx.strokeStyle = config.color.replace('0.5', opacity.toString());
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, variant]);

  return (
    <div className="unified-player-wave">
      <canvas 
        ref={canvasRef} 
        width={400} 
        height={120}
        className="w-full h-full"
      />
    </div>
  );
};

// Main UnifiedPlayer Component
const UnifiedPlayer = ({
  // Context
  context = 'soundscape', // 'radio' | 'soundscape' | 'playlist'
  contextIcon = '🎵',
  contextName = 'Player',
  
  // Current track info
  currentTrack = null, // { display_name, display_name_fr, display_name_en, filename, url }
  
  // Playback state
  isPlaying = false,
  isLoading = false,
  currentTime = 0, // seconds
  duration = 0, // seconds (0 for radio = infinite)
  
  // Callbacks
  onBack,
  onPlayPause,
  onStop,
  onPrev,
  onNext,
  onSeek,
  onFavoriteToggle,
  onMenuClick,
  
  // Favorite state
  isFavorite = false,
  
  // Navigation controls
  hasPrev = false,
  hasNext = false,
  showPrevNext = true, // false for soundscape
  
  // Volume
  volume = 70,
  onVolumeChange,
  
  // Voice Volume (for Radio with voice messages)
  voiceVolume = 30,
  onVoiceVolumeChange,
  showVoiceVolume = false, // Only show for radio context
  
  // Acouphènes settings
  tinnitusEnabled = false,
  onTinnitusToggle,
  tinnitusInfo = null, // { frequency: 8000, gain: -6, type: 'inverse' }
  tinnitusLocked = false, // Premium lock
  hasTinnitusData = false,
  
  // Profil audio settings
  audioProfileEnabled = false,
  onAudioProfileToggle,
  audioProfileInfo = null, // { frequencies: 7 }
  audioProfileLocked = false, // Premium lock
  hasAudioProfileData = false,
  onAudioProfileClick,
  
  // V1.4: Safe Listening badge
  safeListeningStatus = 'safe', // 'safe' | 'warning' | 'danger'
  showSafeListeningBadge = false,
  
  // V1.1.1: Presence mode
  isPresenceMode = false,
  isVoiceActive = false,
  presenceLabel = 'Présence · Hera',
  
  // V1.1: Voice toggle
  voiceEnabled = true,
  onVoiceToggle,
  
  // Premium modal
  onShowPremiumModal,
  
  // Language
  lang = 'fr',
  
  // Background
  backgroundImage = null,
  
  // Translations
  t = {}
}) => {
  const navigate = useNavigate();
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const previousVolume = useRef(volume);

  // Get display name based on language
  const getDisplayName = () => {
    if (!currentTrack) return t.noTrackSelected || 'Aucun son sélectionné';
    
    // Priority: display_name_[lang] > display_name > filename without extension
    if (lang === 'en' && currentTrack.display_name_en) {
      return currentTrack.display_name_en;
    }
    if (lang === 'fr' && currentTrack.display_name_fr) {
      return currentTrack.display_name_fr;
    }
    if (currentTrack.translations) {
      return currentTrack.translations[lang] || currentTrack.translations.fr || currentTrack.display_name;
    }
    if (currentTrack.display_name) {
      return currentTrack.display_name;
    }
    if (currentTrack.name) {
      return currentTrack.name;
    }
    // Fallback: clean filename
    const filename = currentTrack.filename || currentTrack.file || '';
    return filename.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
  };

  // Format time (mm:ss)
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Handle mute toggle
  const handleMuteToggle = () => {
    if (isMuted) {
      onVolumeChange?.(previousVolume.current);
      setIsMuted(false);
    } else {
      previousVolume.current = volume;
      onVolumeChange?.(0);
      setIsMuted(true);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    onVolumeChange?.(newVolume);
    if (newVolume > 0) setIsMuted(false);
  };

  // Handle voice volume change
  const handleVoiceVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    onVoiceVolumeChange?.(newVolume);
  };

  // Handle seek
  const handleSeek = (e) => {
    if (duration > 0 && onSeek) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      onSeek(percent * duration);
    }
  };

  // Context-specific wave variant
  const waveVariant = context === 'radio' ? 'radio' : context === 'playlist' ? 'playlist' : 'soundscape';

  return (
    <div 
      className="unified-player"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Overlay */}
      <div className="unified-player-overlay" />

      {/* Content Container */}
      <div className="unified-player-content">
        
        {/* ========== 1. HEADER ========== */}
        <header className="unified-player-header">
            {/* V1 FUNDAMENTALS:
            Back button disabled.
            A therapeutic session must be ended only via Stop.
            Kept for possible V2 background / mini-player navigation. */}

        {false && (
          <button 
            onClick={handleBack}
            className="unified-player-header-btn"
            aria-label={t.back || 'Retour'}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}

          <div className="unified-player-header-title">
            {/* FIX 1: Hide icon for radio context - redundant with title "Ambient Radio" */}
            {context !== 'radio' && contextIcon && (
              <span className="unified-player-header-icon">{contextIcon}</span>
            )}
            <span className="unified-player-header-text">{contextName}</span>
          </div>

          <div className="unified-player-header-actions">
            {onFavoriteToggle && context !== 'playlist' && (
              <button 
                onClick={() => {
                  // BUILD 55: Debug log for favorite toggle
                  console.log('[Favorites][UI] toggle click', {
                    context,
                    trackName: getDisplayName(),
                    isFavorite,
                    hasCallback: !!onFavoriteToggle
                  });
                  onFavoriteToggle();
                }}
                className={`unified-player-header-btn ${isFavorite ? 'active' : ''}`}
                aria-label={t.favorite || 'Favori'}
                data-testid="favorite-toggle-btn"
              >
                <Star className={`w-6 h-6 ${isFavorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
              </button>
            )}
            {onMenuClick && context === 'playlist' && (
              <button 
                onClick={onMenuClick}
                className="unified-player-header-btn"
                aria-label={t.menu || 'Menu'}
              >
                <MoreVertical className="w-6 h-6" />
              </button>
            )}
          </div>
        </header>

        {/* ========== 2. WAVE ANIMATION ========== */}
        <div className="unified-player-visual">
          <WaveAnimation isPlaying={isPlaying} variant={waveVariant} />
        </div>

        {/* ========== 3. TRACK TITLE ========== */}
        <div className="unified-player-title">
          <h2 className="unified-player-title-text">
            {getDisplayName()}
          </h2>
          
        {/* Unified audio state label */}
        {(isPresenceMode || audioProfileEnabled) && (
          <div
          className="unified-player-guided-label"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
            padding: '5px 12px',
            borderRadius: '16px',
            background: isVoiceActive
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.18) 0%, rgba(99, 102, 241, 0.18) 100%)'
              : 'rgba(255, 255, 255, 0.05)',
            border: isVoiceActive
              ? '1px solid rgba(139, 92, 246, 0.22)'
              : '1px solid rgba(255,255,255,0.06)',
            transition: 'all 0.35s ease',
            opacity: 0.92,
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: isVoiceActive
              ? '0 0 18px rgba(139,92,246,0.10)'
              : 'none',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              fontWeight: '500',
              color: isVoiceActive
                ? 'rgba(224,231,255,0.92)'
                : 'rgba(255,255,255,0.68)',
              letterSpacing: '0.25px',
            }}
          >
            {isPresenceMode
              ? (lang === 'en'
                  ? 'Hera presence · Listening profile active'
                  : 'Présence Hera · Profil d’écoute actif')
              : (lang === 'en'
                  ? 'Listening profile active'
                  : 'Profil d’écoute actif')}
          </span>
        </div>
       )}
        </div>

        {/* ========== 4. TRANSPORT CONTROLS ========== */}
        <div className="unified-player-transport">
          {/* Timeline (hidden for radio) */}
          {context !== 'radio' && duration > 0 && (
            <div className="unified-player-timeline">
              <span className="unified-player-time">{formatTime(currentTime)}</span>
              <div 
                className="unified-player-progress-container"
                onClick={handleSeek}
              >
                <div className="unified-player-progress-bg">
                  <div 
                    className="unified-player-progress-fill"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <span className="unified-player-time">{formatTime(duration - currentTime)}</span>
            </div>
          )}

          {/* Control Buttons */}
          <div className="unified-player-controls">
            {/* Play/Pause */}
            <button
              onClick={onPlayPause}
              disabled={isLoading}
              className="unified-player-control-btn primary"
              aria-label={isPlaying ? (t.pause || 'Pause') : (t.play || 'Lecture')}
            >
              {isLoading ? (
                <div className="unified-player-loader" />
              ) : isPlaying ? (
                <Pause className="w-10 h-10" />
              ) : (
                <Play className="w-10 h-10 ml-1" />
              )}
            </button>

            {/* Next */}
            {showPrevNext && hasNext && (
              <button
                onClick={onNext}
                className="unified-player-control-btn"
                aria-label={t.next || 'Suivant'}
              >
                <SkipForward className="w-8 h-8" />
              </button>
            )}
          </div>
        </div>

        {/* ========== 5. PROFIL AUDIO BLOCK (V1.1 - Source de vérité avec états) ========== */}
        {/* States: 1=No tests, 2=Audio only, 3=Both tests */}
        {(() => {
          // V1.1: Detect test states
          const hasAudioTest = hasAudioProfileData;
          const hasTinnitusTest = hasTinnitusData && tinnitusInfo?.frequency > 0;
          // V1.1 FIX: Use tinnitusInfo.active to determine if tinnitus is currently ON
          const isTinnitusActive = tinnitusInfo?.active === true;
          // V1.1 FIX: Use audioProfileEnabled to determine if audio profile is currently ON
          const isAudioProfileActive = audioProfileEnabled && hasAudioTest;
          const currentState = !hasAudioTest ? 1 : (!hasTinnitusTest ? 2 : 3);
          const isFr = lang !== 'en';
          
          return (
            <div 
              className="unified-player-block clickable"
              onClick={() => {
                if (audioProfileLocked) {
                  onShowPremiumModal?.();
                } else {
                  onAudioProfileClick?.();
                }
              }}
              data-testid="audio-profile-entry"
            >
              <div className="unified-player-block-header">
                <div className="unified-player-block-label">
                  <span>{t.audioProfileLabel || 'Profil audio'}</span>
                  {audioProfileLocked && <Lock className="w-3.5 h-3.5 text-purple-400" />}
                  {!audioProfileLocked && (
                    <span className="unified-player-block-detail">
                      {currentState === 1 
                        ? ` · ${isFr ? 'défaut' : 'default'}`
                        : ` · ${isAudioProfileActive ? 'ON' : 'OFF'}`
                      }
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* V1.1 FIX: Badge reflects actual toggle state, not just profile existence */}
                  {!audioProfileLocked && hasAudioTest && (
                    <span className={`text-xs font-medium ${isAudioProfileActive ? 'text-green-400/80' : 'text-white/40'}`}>
                      {isAudioProfileActive ? (t.enabled || 'Activée') : 'OFF'}
                    </span>
                  )}
                  <Settings className="w-5 h-5 text-white/60" />
                </div>
              </div>
              
              {/* V1.1 FIX: Tinnitus info based on ACTIVE state, not just test existence */}
              {/* BUILD 65: Use ONLY frequency, NO dB for tinnitus display */}
              {!audioProfileLocked && (
                <p className="unified-player-block-info">
                  {hasAudioTest
                    ? (
                      <>
                       {isFr ? 'Personnalisé pour votre profil auditif' : 'Personalized for your hearing profile'}
                       <br />
                       {(audioProfileInfo?.frequencies || [])
                         .filter((band) => Math.abs(Number(band.gain_db ?? band.gain ?? band.value ?? 0)) > 0)
                         .map((band) => `${Math.round(Number(band.frequency ?? band.freq ?? band.hz))} Hz`)
                         .filter(Boolean)
                         .join(' · ')}
                      </>
                  )
                  : (isFr ? 'Écoute naturelle' : 'Natural listening')
                }
              </p>
            )}
              
              {audioProfileLocked && (
                <p className="unified-player-block-info locked">
                  🔒 {t.premiumFeature || 'Fonctionnalité Premium'}
                </p>
              )}
            </div>
          );
        })()}

        {/* ========== 6. FOOTER CONTROLS ========== */}
        <footer className="unified-player-footer">
          {/* V1.1: Voice Toggle */}
          {onVoiceToggle && (
            <div className="unified-player-voice-toggle" data-testid="voice-toggle-container">
              <button
                onClick={onVoiceToggle}
                className={`unified-player-voice-toggle-btn ${voiceEnabled ? 'active' : ''}`}
                data-testid="voice-toggle-btn"
                aria-label={voiceEnabled ? 'Voice ON' : 'Voice OFF'}
              >
                <span className="unified-player-voice-toggle-icon">
                  {voiceEnabled ? '🎙️' : '🔇'}
                </span>
                <span className="unified-player-voice-toggle-label">
                  {lang === 'en' ? 'Voice' : 'Voix'}
                </span>
                <span className={`unified-player-voice-toggle-state ${voiceEnabled ? 'on' : 'off'}`}>
                  {voiceEnabled ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          )}
          
          {/* Volume Controls Container */}
          <div className="unified-player-volume-container">
            {/* Voice Volume Control removed for V1 stability */}
          {/*
          {(showVoiceVolume || isPresenceMode) && onVoiceVolumeChange && (
          <div className="unified-player-volume voice" data-testid="voice-volume-container">
            <div className="unified-player-volume-label">
              <span className="unified-player-volume-icon">🎙️</span>
              <span>{lang === 'en' ? 'Voice' : 'Voix'}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={voiceVolume}
              onChange={handleVoiceVolumeChange}
              className="unified-player-volume-slider voice"
              data-testid="voice-volume-slider"
            />
            <span className="unified-player-volume-value" data-testid="voice-volume-value"></span>
          </div>
        )}
       */}
            
            {/* Music volume fixé à 80% — slider supprimé */}
          </div>

          {/* End Session Button — visible night-safe */}
          <button
            onClick={onStop}
            className="min-h-[48px] px-5 py-3 rounded-2xl bg-white/12 border border-white/25 backdrop-blur-md transition-all duration-200 active:scale-95 shadow-lg"
            aria-label={t.endSession || 'Fin de session'}
          >
            <span className="flex items-center justify-center gap-2 text-sm text-white font-semibold tracking-wide">
              <span aria-hidden="true" className="text-base">🏡</span>
              <span>{t.endSession || 'Fin de session'}</span>
            </span>
          </button>
        </footer>
      </div>
    </div>
  );
};

export default UnifiedPlayer;
